import React, {
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  KeyboardEvent,
} from 'react'
import { useRouter } from 'next/router'
import cn from 'classnames'
import { debounce } from 'lodash'
import { IconSearchDefault, IconArrowRightDefault } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import Link from 'components/Link'
import { searchGladlySupportAnswers } from 'libs/client/api'
import { useHelpCenterEvents } from '../../../events'
import { useIsChangingRoute } from '../../../useIsChangingRoute'
import {
  BrandKey,
  genAnswerSlug,
  productsByKey,
  stripHtmlTags,
} from '../../../utils'
import { useGladlySearchContext } from './GladlySearchContext'
import { useIsSearchFocused } from './useIsSearchFocused'
import classes from './GladlySearchBox.module.scss'

interface GladlySearchBoxProps {
  brandKey: BrandKey
}

export const GladlySearchBox = ({ brandKey }: GladlySearchBoxProps) => {
  const product = productsByKey[brandKey]

  const { trigger } = useHelpCenterEvents()

  const { t } = useTranslation('@mc/next')

  const {
    config,
    setCurrentSearch,
    isSearching,
    setIsSearching,
    results,
    setResults,
    lastSearchPerformed,
    setLastSearchPerformed,
  } = useGladlySearchContext()

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLUListElement>(null)

  const isRedirecting = useIsChangingRoute()

  useEffect(() => {
    if (isRedirecting) {
      setCurrentSearch(null)
      setResults(null)
    }
  }, [isRedirecting, setCurrentSearch, setResults])

  const { query, push } = useRouter()

  // Set initial search value from query
  const initialSearch = query.search ? (query.search as string) : ''

  useEffect(() => {
    inputRef.current!.value = initialSearch
  }, [initialSearch])

  // Input manipulation functions
  const blurInput = () => {
    inputRef.current?.blur()
  }

  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleKeydown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      inputRef.current?.blur()
    }
  }

  const isSearchFocused = useIsSearchFocused(inputRef, dropdownRef)

  const handleSearchForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const search = e.currentTarget.search.value

    blurInput()

    if (search) {
      push({
        pathname: paths.helpCenterForProduct(product.slug),
        query: {
          search,
        },
      })
    }
  }

  const handleSearchResultSelected = () => {
    clearInput()

    if (dropdownRef.current) {
      dropdownRef.current.blur()
    }
  }

  const handleSearchClearButton = () => {
    if (initialSearch) push(paths.helpCenterForProduct(product.slug))

    setResults(null)
    setLastSearchPerformed(null)
    setCurrentSearch(null)
  }

  const handleResumeLastSearch = () => {
    if (inputRef.current && lastSearchPerformed) {
      inputRef.current.value = lastSearchPerformed
      handleChange(lastSearchPerformed)
      inputRef.current.focus()
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchDebounce = useCallback(
    debounce(async (value: string) => {
      if (config && value.length > 2) {
        setIsSearching(true)
        const { results: newResults } = await searchGladlySupportAnswers(
          config.audienceId,
          value!,
          config.lng,
        )
        setIsSearching(false)
        setLastSearchPerformed(value ?? null)
        setResults(newResults ?? [])
        trigger('search', { search: value })
      } else {
        setLastSearchPerformed(null)
        setResults(null)
      }
    }, 500),
    [config],
  )

  const handleChange = (newSearch: string) => {
    setCurrentSearch(newSearch ?? null)

    searchDebounce(newSearch)

    if (newSearch.length > 0 && results && results.length === 0) {
      setResults(null)
    }

    // If the user clears the input
    if (!newSearch) {
      handleSearchClearButton()
    }
  }

  return (
    // Needed for closing on escape key press
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onKeyDown={handleKeydown} className='position-relative'>
      <form
        className={`${classes.form} position-relative mc-mx-auto mc-w-100 d-flex align-items-center mc-mt-5`}
        onSubmit={handleSearchForm}
      >
        {isSearching ? (
          <div className={`${classes.spinner} position-absolute`}>
            <div className='spinner' />
          </div>
        ) : (
          <IconSearchDefault
            alt='Search'
            className='position-absolute'
            size='lg'
          />
        )}
        <input
          ref={inputRef}
          name='search'
          type='search'
          autoComplete='off'
          className='flex-grow-1 mc-text-large mc-corners--3'
          placeholder={t('helpCenter.search.placeholder')}
          onChange={(e) => handleChange(e.target.value)}
        />
      </form>
      <GladlySearchResultsList
        ref={dropdownRef}
        onResumeClick={handleResumeLastSearch}
        onOptionClick={handleSearchResultSelected}
        isSearchFocused={isSearchFocused}
        productSlug={product.slug}
      />
    </div>
  )
}

// GLADLY SEARCH RESULTS LIST
interface GladlySearchResultsListProps {
  onResumeClick: () => void
  onOptionClick?: () => void
  isSearchFocused: boolean
  productSlug: string
}

const GladlySearchResultsList = forwardRef<
  HTMLUListElement,
  GladlySearchResultsListProps
>(({ onResumeClick, onOptionClick, isSearchFocused, productSlug }, ref) => {
  const { t } = useTranslation('@mc/next')
  const {
    currentSearch,
    setCurrentSearch,
    results,
    setResults,
    lastSearchPerformed,
    isSearching,
  } = useGladlySearchContext()

  const { trigger } = useHelpCenterEvents()

  const handleOptionClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    answerName: string,
  ) => {
    trigger('click', {
      action: `Open: ${stripHtmlTags(answerName)}`,
      location: 'Results Dropdown',
    })
    setResults(null)
    setCurrentSearch(null)
    e.currentTarget.blur()
    onOptionClick?.()
  }

  // Tab index is set to -1 to allow programmatic focus
  // Needed for MOBILE KEYBOARD BEHAVIOR in useIsSearchFocused
  return (
    <ul
      tabIndex={-1}
      ref={ref}
      className={cn(classes.suggestions, 'position-absolute', {
        'd-none':
          !isSearchFocused ||
          !(
            (currentSearch && results) ||
            (lastSearchPerformed && !isSearching)
          ),
      })}
    >
      {/* RESUME SEARCH */}
      {!currentSearch && !!lastSearchPerformed && (
        <li key={lastSearchPerformed}>
          <button
            onClick={onResumeClick}
            className='mc-p-4 mc-w-100 d-flex align-items-center justify-content-between'
          >
            {t('helpCenter.search.resume', {
              search: lastSearchPerformed,
            })}
            <IconArrowRightDefault />
          </button>
        </li>
      )}
      {currentSearch && currentSearch.length > 2 && (
        <>
          {/* RESULTS */}
          {results?.map((result) => (
            <li key={result.id}>
              <Link
                className='mc-p-4 mc-w-100 mc-text--left'
                onClick={(e) => handleOptionClick(e, result.name)}
                href={paths.helpCenterAnswer(
                  productSlug,
                  genAnswerSlug(result.id, result.name),
                )}
                dangerouslySetInnerHTML={{ __html: result.name }}
              />
            </li>
          ))}
          {/* NO RESULTS MESSAGE */}
          {results && !results.length && !isSearching && (
            <li className='mc-p-4'>
              {t('helpCenter.search.noResults', {
                search: currentSearch,
              })}
            </li>
          )}
        </>
      )}
    </ul>
  )
})

GladlySearchResultsList.displayName = 'GladlySearchResultsList'
